import httpClient from "@/config/axios";
import { useGroupStore } from "@/store/groupStore";
import { useIsAdmin } from "./useAuth";
import { useQuery } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";

export interface RosterConfigObject {
  config_id: number;
  version?: number | null;
  config_name?: string | null;
  config_memo?: string | null;
  config_version: string | null;
  day_req: number; // 주간 근무 요구 인원
  eve_req: number; // 저녁 근무 요구 인원
  nig_req: number; // 야간 근무 요구 인원
  min_exp_per_shift: number; // 교대당 최소 경력 연수
  req_exp_nurses: number; // 교대당 경력 간호사 필요 인원
  two_offs_per_week: boolean; // 주 2일 휴무 여부
  max_nig_per_month: number; // 월 최대 야간 근무 수
  three_seq_nig: boolean; // N 3연속 허용 여부
  two_offs_after_three_nig: boolean; // N 3회 후 OFF 2일 여부
  two_offs_after_two_nig: boolean; // N 2회 후 OFF 2일 여부
  banned_day_after_eve: boolean; // E 다음 날 D 금지 여부
  max_conseq_work: number; // 최대 연속 근무일 수
  max_conseq_off: number | null; // 최대 연속 OFF 일수
  off_days: number; // OFF 일수
  // shift_priority: number;
  weekend_shift_ratio: number; // 주말 근무 비율
  patient_amount: number; // 환자 수
  sequential_offs: boolean; // 연속 휴무 허용 여부
  even_nights: boolean; // 야간 근무 균등 배정 여부
  created_at: string; // 생성 시각
  updated_at?: string | null;
  nod_noe: boolean; // N-O-D/E 패턴 최소화 여부
  weekly_off_group: boolean; // 주휴 설정
  not_one_night: boolean; // 나이트 연속 배정 (1N 금지)
  off_placement_mode: boolean; // 주휴 인접 OFF 우선배치
  // 프리셉터
  preceptor_gauge: number; // 프리셉터 관련 게이지 - 이제안쓰는듯
  preceptee_on: boolean; // 프리셉티 켜기 여부
  preceptee_shift_count: boolean; // 프리셉티 근무 시간 카운트
  fixed_wanted_use_yn: boolean; // 원티드 관리보드 반영
  use_mid: boolean; // mid 사용 여부

  show_level: boolean; // 직책 표시 여부
  show_preceptor: boolean; // 프리셉터 표시 여부
  off_first: boolean; // OFF 우선배치 여부 - default false

  off_swap_enabled: boolean; // OFF 스왑 허용 여부

  // 휴가 자동 부여. 서버는 미전송(undefined)이면 기존 값을 유지한다.
  health_leave_enabled?: boolean; // 보건휴가 자동 부여
  health_leave_weekend?: boolean; // 보건휴가 주말 배치 허용 (false=평일만)
  sleep_off_enabled?: boolean; // 수면OFF 자동 부여
  sleep_off_cycle?: number | null; // 수면OFF 트리거 N 연번 (실무 15)
  // 확정 원티드 OFF 직전일 N 금지. 자동 배정 OFF 는 대상이 아니다(휴가·공가는 별도 설정).
  ban_night_before_fixed_wanted_off?: boolean;
}

export default function useRosterConfig(options?: {
  readonly enabled?: boolean;
  readonly scheduleId?: string | null;
}) {
  const { selectedGroupId } = useGroupStore();
  const isAdmin = useIsAdmin();

  const effectiveGroupId = isAdmin
    ? (selectedGroupId ?? selectedGroupId ?? "")
    : "";

  return useQuery<RosterConfigObject>({
    queryKey: options?.scheduleId
      ? QUERY_KEYS.config.scheduleConfig(effectiveGroupId, options.scheduleId)
      : QUERY_KEYS.config.configData(effectiveGroupId),
    queryFn: async () => {
      return await httpClient.get<RosterConfigObject>(
        "/roster/config/version/latest",
        {
          params: {
            group_id: effectiveGroupId,
            ...(options?.scheduleId ? { schedule_id: options.scheduleId } : {}),
          },
        }
      );
    },
    enabled: options?.enabled ?? true,
    staleTime: 1000 * 60 * 5, // 5분간 캐시
  });
}
